import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchTimeperiod } from 'app/pages/AutopilotPage/data/salesDemoDataService';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { renderComponentEnum } from 'app/pages/SalesPortalPage/LayoutV1/data/type';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import type { MetricsFrameworkPeriod } from '../types';
import { getTileLoadProgress } from '../utils/tileLoadProgress';
import {
  arePeriodsEqual,
  getFallbackPeriodFromOptions,
  isActiveTimePeriodSelectionValid,
  resolveSharedTimePeriod,
  storeTimePeriod,
} from '../utils/timePeriodPersistence';

type DealerWithTimePeriodPreference = {
  preferences?: { default_time_period?: string };
};

export const useMetricsFrameworkTimePeriod = () => {
  const { dealerId, period, setPeriod, mockApiFlag } =
    useMetricsFrameworkContext();
  const selectedDealer = useSelector(selectDealerSelected);
  const [timeperiodOptions, setTimeperiodOptions] = useState<
    Array<{ code: string; name: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const hydratedForDealerRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadTimeperiodOptions = async () => {
      setLoading(true);
      try {
        const options = await fetchTimeperiod({ useMockData: mockApiFlag });
        if (cancelled) return;
        setTimeperiodOptions(Array.isArray(options) ? options : []);
      } catch {
        if (!cancelled) {
          setTimeperiodOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTimeperiodOptions();

    return () => {
      cancelled = true;
    };
  }, [mockApiFlag]);

  useEffect(() => {
    if (!dealerId || timeperiodOptions.length === 0) return;

    const dealerDefaultTimePeriod =
      selectedDealer && typeof selectedDealer === 'object'
        ? (selectedDealer as DealerWithTimePeriodPreference).preferences
            ?.default_time_period
        : undefined;

    if (hydratedForDealerRef.current === dealerId) {
      if (
        isActiveTimePeriodSelectionValid(period.timePeriod, timeperiodOptions)
      ) {
        return;
      }

      const fallbackPeriod = getFallbackPeriodFromOptions(timeperiodOptions);
      if (!fallbackPeriod) return;

      setPeriod(fallbackPeriod);
      storeTimePeriod(dealerId, fallbackPeriod.timePeriod);
      return;
    }

    const resolved =
      resolveSharedTimePeriod({
        dealerId,
        options: timeperiodOptions,
        dealerDefaultTimePeriod,
      }) ??
      (isActiveTimePeriodSelectionValid(period.timePeriod, timeperiodOptions)
        ? period
        : getFallbackPeriodFromOptions(timeperiodOptions));

    if (!resolved) return;

    if (!arePeriodsEqual(period, resolved)) {
      setPeriod(resolved);
    }
    storeTimePeriod(
      dealerId,
      resolved.timePeriod,
      resolved.startDate,
      resolved.endDate,
    );
    hydratedForDealerRef.current = dealerId;
  }, [dealerId, period, selectedDealer, setPeriod, timeperiodOptions]);

  const handleClickChangeTimeperiod = useCallback(
    (code: string, label?: string, startDate?: string, endDate?: string) => {
      if (getTileLoadProgress().active > 0) return;

      const matched = timeperiodOptions.find(option => option.code === code);
      const nextPeriod: MetricsFrameworkPeriod = {
        timePeriod: code,
        label: label ?? matched?.name ?? code,
      };

      if (startDate) nextPeriod.startDate = startDate;
      if (endDate) nextPeriod.endDate = endDate;

      setPeriod(nextPeriod);
      if (dealerId) {
        storeTimePeriod(dealerId, code, startDate, endDate);
      }
    },
    [dealerId, setPeriod, timeperiodOptions],
  );

  return {
    timeperiod: timeperiodOptions,
    selectedTimeperiod: period.timePeriod,
    handleClickChangeTimeperiod,
    timeperiodLoading: loading,
    renderComponent: renderComponentEnum.metrics,
  };
};
